// Reusable Cammy action library — drives the rig built by cammy-model.js.
// Works on any page: const actor = createCammyActor(THREE, scene, {onSay, onFlash});
// call actor.update(seconds) each frame, actor.play('run'|'flip'|...), actor.setBounds(minX, maxX).
import { buildCammy } from './cammy-model.js';
import { buildVehicle } from './sedan-model.js';

export const CAMMY_ACTIONS = ['run', 'flip', 'fact', 'laptop', 'snapCar', 'snapHouse', 'point'];
export const FACTS = [
  'Our large vision model was trained on 450+ million customer-validated images.',
  'We spot paint defects down to 50 microns — finer than a human hair.',
  'Motor-insurance claims settle in under 5 minutes with CamCom damage AI.',
  '70% of the world’s products are metal, plastic, glass or rubber — we inspect them all.',
  'CamCom inspects cars, warehouses, aircraft, pharma labels and even city streets.',
  'CamCom has been eyeing the future since 2017 — born in Bengaluru, seen worldwide.',
];

export function createCammyActor(THREE, scene, { onSay = () => {}, onFlash = () => {}, autoPlay = true, logoUrl = 'uploads/camcom-logo.png' } = {}) {
  const { group: cammy, tick, rig } = buildCammy(THREE, { logoUrl });
  scene.add(cammy);
  const shadow = new THREE.Mesh(new THREE.CircleGeometry(0.32, 28), new THREE.MeshBasicMaterial({ color: 0x14161a, transparent: true, opacity: 0.14 }));
  shadow.rotation.x = -Math.PI / 2; shadow.position.y = 0.005; scene.add(shadow);

  const P = {
    white: new THREE.MeshStandardMaterial({ color: 0xf2f3f1, roughness: 0.4, metalness: 0.1 }),
    dark: new THREE.MeshStandardMaterial({ color: 0x1c1e22, roughness: 0.4, metalness: 0.2 }),
    glassy: new THREE.MeshStandardMaterial({ color: 0x2a3138, roughness: 0.12, metalness: 0.3 }),
    glow: new THREE.MeshBasicMaterial({ color: 0xfff6ee }),
  };
  const nm = (o, n) => { o.name = n; return o; };

  const laptop = new THREE.Group(); laptop.name = 'laptop'; laptop.visible = false; scene.add(laptop);
  {
    const base = nm(new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.02, 0.25), P.dark), 'laptop_base'); base.position.y = 0.01;
    const lid = nm(new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.24, 0.014), P.dark), 'laptop_lid');
    lid.position.set(0, 0.105, -0.145); lid.rotation.x = 0.32;
    const screen = nm(new THREE.Mesh(new THREE.PlaneGeometry(0.32, 0.2), P.glow), 'laptop_screen');
    screen.position.set(0, 0.105, -0.137); screen.rotation.x = 0.32;
    const logo = nm(new THREE.Mesh(new THREE.PlaneGeometry(0.07, 0.07), rig.mats.red), 'laptop_screen_logo');
    logo.position.set(0, 0.115, -0.135); logo.rotation.x = 0.32;
    laptop.add(base, lid, screen, logo);
  }
  const camProp = new THREE.Group(); camProp.name = 'hand_camera'; camProp.visible = false;
  {
    camProp.add(nm(new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.085, 0.07), P.dark), 'cam_body'));
    const lens = nm(new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.05, 20), P.glassy), 'cam_lens');
    lens.rotation.x = Math.PI / 2; lens.position.set(0, 0, 0.05); camProp.add(lens);
    const ring = nm(new THREE.Mesh(new THREE.TorusGeometry(0.033, 0.007, 10, 22), rig.mats.red), 'cam_lens_ring');
    ring.position.set(0, 0, 0.072); camProp.add(ring);
    const shut = nm(new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.015, 14), rig.mats.red), 'cam_shutter');
    shut.position.set(0.04, 0.05, 0); camProp.add(shut);
  }
  rig.shoulderR.add(camProp); camProp.position.set(0, -0.235, 0.05);
  const flashMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false });
  const flashPlane = nm(new THREE.Mesh(new THREE.PlaneGeometry(0.5, 0.5), flashMat), 'flash_plane');
  flashPlane.position.set(0, 0, 0.09); camProp.add(flashPlane);

  // car prop: the detailed sedan model at toy scale
  const car = new THREE.Group(); car.name = 'car_prop'; car.visible = false; scene.add(car);
  const carWheels = [];
  {
    const sedan = buildVehicle().model;
    sedan.scale.setScalar(0.4);
    sedan.traverse((o) => {
      // only the inner wheel groups (positioned at the axles) — their same-named
      // part wrappers sit at the car origin, and rotating those orbits the wheel
      if (/vehicle_(front|rear)_(left|right)_wheel$/.test(o.name) && o.position.lengthSq() > 0.01) carWheels.push(o);
    });
    car.add(sedan);
  }
  const house = new THREE.Group(); house.name = 'mini_house'; house.visible = false; scene.add(house);
  {
    const walls = nm(new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.62, 0.7), P.white), 'house_walls'); walls.position.y = 0.31;
    const roof = nm(new THREE.Mesh(new THREE.ConeGeometry(0.72, 0.36, 4), rig.mats.red), 'house_roof');
    roof.rotation.y = Math.PI / 4; roof.position.y = 0.8;
    const door = nm(new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.3, 0.02), rig.mats.red), 'house_door'); door.position.set(0.12, 0.15, 0.36);
    const win = nm(new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.16, 0.02), P.glassy), 'house_window'); win.position.set(-0.2, 0.36, 0.36);
    house.add(walls, roof, door, win);
  }

  /* ---- action machinery ---- */
  const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
  const ss = (x) => x * x * (3 - 2 * x);
  let action = null, now = 0, factIdx = 0;
  let bounds = { minX: -2.2, maxX: 2.2 };
  let auto = autoPlay, nextIdle = 8;

  // ---- lean-toward-target — self-contained, safe to delete ----------------
  // Lets a caller (e.g. the Cases list, narrating whichever row is under the
  // pointer) make Cammy glance up or down toward it. Deliberately NOT part of
  // the action system above: it never calls setAction/resetPose, and it only
  // ADDS a small tilt to rig.head.rotation.x after tick() and any action's own
  // update() have already set it for the frame — never overwrites either, so
  // a lean starting, changing or being cleared never interrupts or resets a
  // run/flip/point/etc already in flight, and this whole block can be deleted
  // (here, its three lines in update() below, and the setLean export) without
  // touching anything else in this file.
  let leanTarget = 0; // -1 look up, 0 neutral, 1 look down
  let leanCurrent = 0;
  let leanLastT = 0;
  const LEAN_MAX_TILT = 0.22; // radians — a glance, not a bow
  const LEAN_EASE_PER_SEC = 5;

  function resetPose() {
    cammy.position.y = 0; cammy.rotation.x = 0; cammy.rotation.y = 0;
    rig.hipL.rotation.x = rig.hipR.rotation.x = 0;
    rig.body.rotation.x = 0; rig.head.rotation.x = 0;
    shadow.scale.setScalar(1);
    flashMat.opacity = 0; onFlash(0);
    laptop.visible = car.visible = house.visible = camProp.visible = false;
  }
  function setAction(a) { if (action) resetPose(); action = a; action.t0 = now; }

  function runTo(targetX, then) {
    targetX = clamp(targetX, bounds.minX, bounds.maxX);
    const fromX = cammy.position.x;
    const dist = Math.abs(targetX - fromX);
    if (dist < 0.15) { then && then(); return; }
    const dur = clamp(dist / 2.3, 0.5, 4);
    const dirn = Math.sign(targetX - fromX);
    setAction({ name: 'run', dur, update(k) {
      const x = fromX + (targetX - fromX) * ss(k);
      const trav = Math.abs(x - fromX);
      cammy.position.x = x;
      cammy.rotation.y = dirn * Math.PI / 2 * ss(clamp(k * 6, 0, 1)) * (1 - ss(clamp((k - 0.85) / 0.15, 0, 1)));
      const ph = trav * 11;
      const sw = Math.sin(ph) * 0.85 * Math.sin(clamp(k * 4, 0, 1) * Math.PI / 2);
      rig.hipL.rotation.x = sw; rig.hipR.rotation.x = -sw;
      rig.shoulderL.rotation.x = -sw * 0.8; rig.shoulderR.rotation.x = sw * 0.8;
      rig.body.position.y += Math.abs(Math.sin(ph)) * 0.05;
      rig.body.rotation.x = 0.14;
    }, end() {
      cammy.rotation.y = 0; rig.hipL.rotation.x = rig.hipR.rotation.x = 0; rig.body.rotation.x = 0;
      then && then();
    } });
  }
  function flip() {
    setAction({ name: 'flip', dur: 1.1, update(k) {
      const j = Math.sin(k * Math.PI);
      cammy.position.y = j * 0.62;
      cammy.rotation.x = -(k < 0.08 ? 0 : k > 0.92 ? 1 : (k - 0.08) / 0.84) * Math.PI * 2;
      shadow.scale.setScalar(1 - j * 0.45);
    }, end() { cammy.position.y = 0; cammy.rotation.x = 0; shadow.scale.setScalar(1); onSay('Ta-da!', 2); } });
  }
  function fact() {
    onSay(FACTS[factIdx++ % FACTS.length], 6);
    setAction({ name: 'fact', dur: 3.2, update(k) {
      const w = Math.sin(Math.min(k * 3, 1) * Math.PI / 2);
      rig.shoulderL.rotation.z = -0.12 - w * 0.9; rig.shoulderL.rotation.x = -w * 0.5;
    }, end() {} });
  }
  function laptopTime() {
    onSay('One sec — uploading inspection reports…', 4);
    setAction({ name: 'laptop', dur: 6, update(k) {
      const w = Math.sin(Math.min(k * 5, 1) * Math.PI / 2);
      rig.body.position.y -= w * 0.16;
      rig.hipL.rotation.x = -w * 1.1; rig.hipR.rotation.x = -w * 1.1;
      cammy.rotation.y = w * 0.55;
      rig.shoulderL.rotation.x = -w * (0.85 + 0.1 * Math.sin(now * 13));
      rig.shoulderR.rotation.x = -w * (0.85 + 0.1 * Math.sin(now * 13 + 2));
      rig.head.rotation.x = w * 0.42;
      P.glow.color.setHex(Math.sin(now * 9) > 0.6 ? 0xffe9e5 : 0xfff6ee);
    }, end() { laptop.visible = false; cammy.rotation.y = 0; onSay('Reports filed. All clear!', 3); } });
    laptop.visible = true;
    laptop.position.set(cammy.position.x + 0.42, 0, 0.25);
    laptop.rotation.y = 0.9;
  }
  function snap(target) {
    const prop = target === 'car' ? car : house;
    const px = cammy.position.x;
    const mid = (bounds.minX + bounds.maxX) / 2;
    const propX = px > mid ? px - 1.7 : px + 1.7;
    const fromX = px > mid ? bounds.minX - 2.2 : bounds.maxX + 2.2;
    const travelDir = Math.sign(propX - fromX) || 1;
    onSay(target === 'car' ? 'Hold still… capturing every panel for the damage report.' : 'Property inspection — capturing the exterior now.', 7);
    setAction({ name: 'snap', dur: 7, update(k) {
      const a = ss(clamp(k / 0.28, 0, 1));
      const dpt = ss(clamp((k - 0.82) / 0.18, 0, 1));
      prop.position.x = fromX + (propX - fromX) * a;
      if (target === 'car') {
        const moving = a < 1 || dpt > 0;
        if (moving) carWheels.forEach((w) => { w.rotation.x += 0.32; });
      } else { prop.scale.setScalar(Math.max(0.001, 0.2 + 0.8 * a)); prop.position.y = 0; }
      const w = ss(clamp((k - 0.25) / 0.15, 0, 1)) * (1 - ss(clamp((k - 0.8) / 0.15, 0, 1)));
      camProp.visible = k > 0.22 && k < 0.95;
      cammy.rotation.y = (propX < px ? -0.9 : 0.9) * w;
      rig.shoulderR.rotation.x = -1.5 * w;
      rig.shoulderR.rotation.z = 0.12 + 0.25 * w;
      rig.head.rotation.x = 0.08 * w;
      const fk = (k - 0.42) / 0.3;
      const strobe = fk > 0 && fk < 1 ? Math.max(0, Math.sin(fk * Math.PI * 6)) : 0;
      flashMat.opacity = strobe * 0.85;
      onFlash(strobe > 0.85 ? 0.55 : 0);
      const dpt2 = dpt;
      if (target === 'car') prop.position.x = propX + dpt2 * travelDir * Math.abs(fromX - propX) * 1.1;
      else prop.scale.setScalar(Math.max(0.001, (0.2 + 0.8 * a) * (1 - dpt2)));
    }, end() {
      prop.visible = false; camProp.visible = false; cammy.rotation.y = 0;
      flashMat.opacity = 0; onFlash(0);
      onSay(target === 'car' ? 'Scan complete — no damage found on this one!' : 'Property documented — every angle covered!', 4);
    } });
    // staged AFTER setAction: an interrupt's resetPose has already run, so it can't wipe these
    prop.visible = true;
    prop.position.set(fromX, 0, 0.1);
    if (target === 'car') car.rotation.y = travelDir * Math.PI / 2; // sedan faces its direction of travel
  }
  function point() {
    onSay('Right up there!', 4);
    setAction({ name: 'point', dur: 3.4, update(k) {
      const w = Math.sin(Math.min(k * 3, 1) * Math.PI / 2);
      rig.shoulderR.rotation.z = 0.12 + w * 2.75;
      rig.head.rotation.x = -w * 0.3;
    }, end() {} });
  }

  function play(name) {
    if (name === 'run') runTo(cammy.position.x > (bounds.minX + bounds.maxX) / 2 ? bounds.minX : bounds.maxX, () => onSay('Phew! Good cardio.', 2.5));
    else if (name === 'flip') flip();
    else if (name === 'fact') fact();
    else if (name === 'laptop') laptopTime();
    else if (name === 'snapCar') snap('car');
    else if (name === 'snapHouse') snap('house');
    else if (name === 'point') point();
    else if (name === 'runTo') { /* use actor.runTo(x, cb) directly */ }
  }
  function idleKick() {
    const opts = ['fact', 'flip', 'fact', 'run', 'laptop', 'snapCar', 'snapHouse'];
    play(opts[Math.floor(Math.random() * opts.length)]);
    nextIdle = now + 15 + Math.random() * 10;
  }

  function update(t) {
    now = t;
    tick(t);
    if (action) {
      const k = (t - action.t0) / action.dur;
      if (k >= 1) { const a = action; action = null; a.end && a.end(); }
      else action.update(k);
      rig.beamMat.opacity = 0; // no idle scan beam during actions
    } else if (auto && t > nextIdle) idleKick();
    shadow.position.x = cammy.position.x;

    // Lean: additive only — see the block above for why this can't fight tick()
    // or an in-progress action.
    const leanDt = clamp(t - leanLastT, 0, 0.1);
    leanLastT = t;
    leanCurrent += (leanTarget - leanCurrent) * Math.min(1, leanDt * LEAN_EASE_PER_SEC);
    rig.head.rotation.x += leanCurrent * LEAN_MAX_TILT;
  }

  return {
    cammy, rig, update, play, runTo,
    actions: CAMMY_ACTIONS,
    setBounds: (minX, maxX) => { bounds = { minX, maxX }; },
    setAutoPlay: (v) => { auto = v; if (v) nextIdle = now + 6; },
    isBusy: () => !!action,
    currentAction: () => action && action.name,
    // -1 look up, 0 neutral/off, 1 look down — see the lean block above.
    setLean: (direction) => { leanTarget = clamp(direction || 0, -1, 1); },
  };
}
